import { useMemo, useState, type FormEvent } from 'react';
import { Image as ImageIcon, Sparkles, Trash2, Loader2, AlertCircle, Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useGenerateImage, useImagePricing, useRemoveImage } from '@/hooks/useImages';
import { useConfirm } from '@/components/ConfirmDialog';
import { ProviderModelSelect } from '@/components/ProviderModelSelect';
import { ApiError } from '@/lib/api-client';
import { cn, formatUsd } from '@/lib/utils';

const IMG_FAMILY_LABEL: Record<string, string> = {
  flux: 'Black Forest Labs (Flux)',
  'dall-e': 'OpenAI (DALL-E + GPT Image)',
  imagen: 'Google (Imagen)',
  'gemini-image': 'Google (Gemini Image / Nano Banana)',
  'stable-diffusion': 'Stability AI (Stable Diffusion)',
  seedream: 'ByteDance (Seedream)',
  wan: 'Alibaba (Wan)',
  'qwen-image': 'Alibaba (Qwen Image / Z-Image)',
  'grok-image': 'xAI (Grok Imagine)',
  recraft: 'Recraft',
  reve: 'Reve',
  kling: 'Kling AI',
  tencent: 'Tencent (Hunyuan)',
  other: 'Diğer',
};
const IMG_FAMILY_ORDER = [
  'flux', 'dall-e', 'imagen', 'gemini-image', 'stable-diffusion',
  'seedream', 'wan', 'qwen-image', 'grok-image', 'recraft', 'reve', 'kling', 'tencent', 'other',
];

export function FeaturedImagePanel({
  contentId,
  r2Key,
  altText,
  focusKeyword,
  templateHasImagePrompt,
}: {
  contentId: string;
  r2Key: string | null | undefined;
  altText: string | null | undefined;
  focusKeyword: string | null | undefined;
  templateHasImagePrompt?: boolean;
}) {
  const pricing = useImagePricing();
  const generate = useGenerateImage();
  const remove = useRemoveImage();
  const confirm = useConfirm();

  // Default to template prompt if available, else manual
  const [useTemplate, setUseTemplate] = useState<boolean>(!!templateHasImagePrompt);
  const [prompt, setPrompt] = useState(
    focusKeyword ? `High quality feature photo illustrating: ${focusKeyword}. Clean, modern, editorial style.` : '',
  );
  const [pricingId, setPricingId] = useState('gemini-2-5-flash-image');
  const [alt, setAlt] = useState(altText ?? '');
  const [error, setError] = useState<string | null>(null);

  const selectedPrice = pricing.data?.pricing.find((p) => p.id === pricingId);
  const pricingGroups = useMemo(() => {
    if (!pricing.data) return [];
    const g: Record<string, typeof pricing.data.pricing> = {};
    for (const p of pricing.data.pricing) (g[p.family] = g[p.family] ?? []).push(p);
    return IMG_FAMILY_ORDER.filter((f) => g[f]?.length).map((fid) => ({
      id: fid,
      label: IMG_FAMILY_LABEL[fid] ?? fid,
      items: (g[fid] ?? []).map((p) => ({
        id: p.id,
        label: p.label,
        suffix: `$${(p.credits / 1_000_000).toFixed(2)}${p.note ? ` · ${p.note}` : ''}`,
        deprecated: p.deprecated,
      })),
    }));
  }, [pricing.data]);

  const imageUrl = r2Key ? `/api/images/${r2Key}` : null;

  const onGenerate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await generate.mutateAsync({
        content_id: contentId,
        prompt: useTemplate ? undefined : prompt,
        use_template: useTemplate,
        pricing_id: pricingId,
        alt_text: alt || undefined,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Görsel üretilemedi');
    }
  };

  const onRemove = async () => {
    const ok = await confirm({
      title: 'Görseli kaldır?',
      description: 'Öne çıkan görsel silinecek. Yeniden üretebilirsin.',
      confirmText: 'Kaldır',
      danger: true,
    });
    if (!ok) return;
    await remove.mutateAsync(contentId);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ImageIcon className="w-4 h-4" /> Öne Çıkan Görsel
        </CardTitle>
        <CardDescription>
          {imageUrl
            ? 'Yayınlanırken WordPress media kütüphanesine yüklenir ve featured image olarak atanır.'
            : 'AI ile featured image üret — seçtiğin sağlayıcı/model ile.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {imageUrl && (
          <div className="space-y-2">
            <img
              src={imageUrl}
              alt={altText ?? ''}
              className="w-full rounded-lg border object-cover max-h-80"
            />
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onRemove} disabled={remove.isPending}>
                <Trash2 className="w-3.5 h-3.5" /> Kaldır
              </Button>
              <span className="text-xs text-muted-foreground">
                {altText ? `Alt: ${altText}` : 'Alt text yok'}
              </span>
            </div>
          </div>
        )}

        <form onSubmit={onGenerate} className="space-y-3">
          {templateHasImagePrompt && (
            <div className="flex items-start gap-2 rounded-md border bg-muted/30 px-3 py-2.5">
              <input
                id="img-use-template"
                type="checkbox"
                checked={useTemplate}
                onChange={(e) => setUseTemplate(e.target.checked)}
                className="h-4 w-4 rounded border-input mt-0.5"
              />
              <div className="flex-1">
                <Label htmlFor="img-use-template" className="cursor-pointer text-sm font-medium">
                  Şablon promptunu kullan
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Bu içerik türünün önceden ayarlanmış görsel promptu kullanılır — başlık ve anahtar kelime otomatik yerleştirilir.
                </p>
              </div>
            </div>
          )}
          {!useTemplate && (
            <div className="space-y-2">
              <Label htmlFor="img-prompt">{imageUrl ? 'Yeni prompt (yenilemek için)' : 'Prompt'}</Label>
              <textarea
                id="img-prompt"
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Detaylı ve açıklayıcı olsun. İngilizce daha iyi sonuç verir."
                required
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>Görsel Modeli</Label>
            <ProviderModelSelect
              groups={pricingGroups}
              value={pricingId}
              onChange={setPricingId}
              providerLabel="Sağlayıcı"
              modelLabel="Model"
            />
            {selectedPrice?.note && (
              <p className="text-xs text-muted-foreground">{selectedPrice.note}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="img-alt">Alt Text (SEO)</Label>
            <Input
              id="img-alt"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder={focusKeyword ?? ''}
            />
          </div>
          {selectedPrice && (
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm flex items-center gap-2">
              <Coins className="w-4 h-4 text-primary" />
              <strong className="font-mono">{formatUsd(selectedPrice.credits)}</strong>
            </div>
          )}
          {error && (
            <div className={cn('text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2 flex items-start gap-2')}>
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={generate.isPending || (!useTemplate && prompt.trim().length < 5)}
          >
            {generate.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generate.isPending ? 'Üretiliyor…' : imageUrl ? 'Yeniden Üret' : 'Görsel Üret'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
